'use client';


interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'gray' | 'locked' | 'outline' | 'underline' | 'link';
  size?: 'sm' | 'md' | 'lg' | 'wide' | 'icon';
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
}

const variantClasses: Record<string, string> = {
  primary: 'bg-purple-500 text-white hover:bg-purple-500/90 disabled:bg-purple-300',
  secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  ghost: 'hover:bg-accent hover:text-accent-foreground',
  gray: 'bg-gray-600 text-white hover:bg-gray-600/90',
  locked: 'bg-grayscale-200 text-white cursor-not-allowed',
  outline: 'border border-input bg-background hover:bg-accent',
  underline: 'text-gray-600 text-[1.375rem] underline',
  link: 'text-primary underline-offset-4 hover:underline',
};

const sizeClasses: Record<string, string> = {
  sm: 'h-9 rounded-md px-3',
  md: 'rounded-[10px] px-5 py-2 text-[1rem]',
  lg: 'h-11 rounded-md px-8',
  wide: 'h-[3.375rem] rounded-[10px] px-8 md:px-[10.1875rem] py-[0.5625rem] text-[1rem]',
  icon: 'h-10 w-10 rounded-md',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  children,
  onClick,
  type = 'button',
  className,
}: ButtonProps) {
  const classes = [
    'inline-flex items-center justify-center gap-2 min-h-[44px] min-w-[44px] border-2 border-transparent font-baloo font-semibold cursor-pointer transition-colors duration-150 leading-none whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    variantClasses[variant] ?? '',
    sizeClasses[size] ?? '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-disabled={disabled || undefined}
    >
      {loading ? (
        <span className="inline-block w-5 h-5 border-2 border-current rounded-full animate-spin" aria-hidden="true" />
      ) : (
        children
      )}
    </button>
  );
}
